import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import GameExpiredRedirect from '@components/GameExpiredRedirect/index';

import { Box, Typography } from '@mui/material';

import { UnityComponent } from '@shared/components';
import { isGameExpired } from '@shared/utils/gameExpiration';

import {
  useFetchGameBuild,
  useFetchGameDefaultsLazy,
} from '../Client/client.actions';

import { useIncreaseGameBuildRunCount } from './game.actions';

import './index.scss';

let isLocalhost = window.location.hostname === 'localhost';

const GamePage = () => {
  const [searchParams] = useSearchParams();
  const [response, increaseGameBuildRunCount, loading] = useIncreaseGameBuildRunCount();
  const [gameUrlPack, setGameUrlPack] = useState<string | null>(null);
  const [webGPUSupported, setWebGPUSupported] = useState<boolean | null>(null);

  const [gameKeyInfo, setGameKeyInfo] = useState<any>(null);
  const [gameKeyLoading, setGameKeyLoading] = useState(true);

  const currentBuildId = searchParams.get('buildId');

  // Получаем инфу о ключе по buildId
  useEffect(() => {
    if (currentBuildId) {
      setGameKeyLoading(true);
      fetch(`/api/game-key/by-build/${currentBuildId}`)
        .then(res => res.json())
        .then(data => {
          setGameKeyInfo(data.gameKey);
          setGameKeyLoading(false);
        })
        .catch(() => setGameKeyLoading(false));
    }
  }, [currentBuildId]);

  // Проверка поддержки WebGPU
  useEffect(() => {
    const checkWebGPUSupport = () => {
      try {
        // Простая проверка наличия WebGPU API без вызова requestAdapter
        const hasWebGPU = 'gpu' in navigator && 
                         (navigator as any).gpu && 
                         typeof (navigator as any).gpu.requestAdapter === 'function';
        
        setWebGPUSupported(hasWebGPU);
        
        if (!hasWebGPU) {
          console.warn('WebGPU API not available on this device');
        }
      } catch (error) {
        console.warn('Error checking WebGPU support:', error);
        setWebGPUSupported(false);
      }
    };

    checkWebGPUSupport();
  }, []);

  useEffect(() => {
    if (currentBuildId !== null) {
      increaseGameBuildRunCount(currentBuildId);
    }
  }, [currentBuildId, increaseGameBuildRunCount]);

  useEffect(() => {
    if (!response) {
      console.log('no response');
      return;
    }

    const pack = (response as any).gameUrlPack;
    setGameUrlPack(pack);
    
    console.log('response ', response);
    console.log('gamePack', pack);
  }, [response]);

  const unityResolution = 1920 / 1080;

  // Проверка статуса игры
  if (gameKeyLoading) {
    return <div>Loading...</div>;
  }
  
  console.log('🔍 Game page check:', {
    gameKeyInfo,
    isActive: gameKeyInfo?.isActive,
    activateDate: gameKeyInfo?.activateDate,
    gameDayCount: gameKeyInfo?.gameDayCount,
    isExpired: gameKeyInfo ? isGameExpired(gameKeyInfo.activateDate, gameKeyInfo.gameDayCount) : null
  });
  
  if (gameKeyInfo && (!gameKeyInfo.isActive || isGameExpired(gameKeyInfo.activateDate, gameKeyInfo.gameDayCount))) {
    console.log('🔍 Game page: Redirecting to GameExpiredRedirect');
    return <GameExpiredRedirect />;
  }

  // Ждём загрузки gameUrlPack и проверки WebGPU
  if (loading || gameUrlPack === null || webGPUSupported === null) {
    return <div>Loading...</div>;
  }

  // Показываем предупреждение для устройств без поддержки WebGPU
  if (webGPUSupported === false) {
    return (
      <Box className="mainPage" sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          Предупреждение о совместимости
        </Typography>
        <Typography sx={{ marginBottom: 3, maxWidth: '500px' }}>
          Ваше устройство может не поддерживать современные графические технологии. 
          Игра может работать медленнее или с ошибками.
        </Typography>
        <button 
          onClick={() => setWebGPUSupported(true)} // Продолжаем загрузку
          style={{
            padding: '12px 24px',
            backgroundColor: '#f3c1c8',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Продолжить загрузку
        </button>
      </Box>
    );
  }

  interface UrlConfig {
    loaderUrl: string;
    dataUrl: string;
    frameworkUrl: string;
    codeUrl: string;
  }
  
  let urlConfig: UrlConfig | Record<string, UrlConfig>;
  
  // isLocalhost = false;
  if (isLocalhost) {
    urlConfig = {
      loaderUrl: import.meta.env.VITE_LOADER as string,
      dataUrl: import.meta.env.VITE_DATA as string,
      frameworkUrl: import.meta.env.VITE_FRAMEWORK as string,
      codeUrl: import.meta.env.VITE_CODE as string,
    };
  } else {
    urlConfig = new Proxy<Record<string, UrlConfig>>({}, {
      get(target, key: string) {
        key = String(key); // Ensure key is a string
        if (key === 'default') key = '001'; // Default fallback
  
        // commit
        return {
          loaderUrl: `${import.meta.env.VITE_URL}${key}/loader.js`,
          dataUrl: `${import.meta.env.VITE_URL}${key}/data.br`,
          frameworkUrl: `${import.meta.env.VITE_URL}${key}/framework.js.br`,
          codeUrl: `${import.meta.env.VITE_URL}${key}/wasm.br`,
        };
      },
    });
  }
  

const gameKey = gameUrlPack || '001'; // Если gameUrlPack null, подставляем '001'
  const selectedConfig =
    isLocalhost ? (urlConfig as UrlConfig) : (urlConfig as Record<string, UrlConfig>)[gameKey];

  const { loaderUrl, dataUrl, frameworkUrl, codeUrl } = selectedConfig;

console.log('Vite URL:', import.meta.env.VITE_URL);
console.log('Loader URL:', loaderUrl);

  return (
    <Box className="mainPage">
      <UnityComponent
        loaderUrl={loaderUrl}
        dataUrl={dataUrl}
        frameworkUrl={frameworkUrl}
        codeUrl={codeUrl}
        resolution={unityResolution}
      />
    </Box>
  );
};

export default GamePage;
